import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './MobileDocumentEditPage.css';
import { DocumentApi, DocumentMetadataApi } from '../../services/ApiService';
import { DocumentEndeavorApi } from '../../services/api/DocumentEndeavorAPI';

// The order you want for your fields
const fieldOrder = [
  "Endeavor",
  "Title",
  "Author",
  "Date",
  "Important Names & Events",
  "Important Names and Events",
  "Key Topics",
  "Summary",
  "Document Text",
  "Document Text Translated from English to Spanish",
  "Notes",
];

// Sort metadata by fieldOrder
function sortMetadata(metaArray) {
  const getIndex = (fieldName) => {
    const i = fieldOrder.findIndex(
      (f) => f.toLowerCase() === fieldName.toLowerCase()
    );
    return i === -1 ? 9999 : i;
  };

  return [...metaArray].sort((a, b) => {
    const nameA = a.metadata_definition?.name || a.metadataName || "";
    const nameB = b.metadata_definition?.name || b.metadataName || "";
    const iA = getIndex(nameA);
    const iB = getIndex(nameB);
    if (iA !== iB) return iA - iB;
    return nameA.localeCompare(nameB);
  });
}

export default function MobileDocumentEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [associatedCourses, setAssociatedCourses] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes in metadata
  const [metadataChanges, setMetadataChanges] = useState({});

  // Load document and metadata
  useEffect(() => {
    let abort = false;

    const loadDocument = async () => {
      console.log('MobileDocumentEdit: Loading document with id:', id, 'user:', user);
      
      if (!id) {
        console.error('MobileDocumentEdit: No document ID provided');
        setError('No document ID provided');
        setLoading(false);
        return;
      }

      if (!user?.id && !user?.userId) {
        console.error('MobileDocumentEdit: No user ID available, user object:', user);
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('MobileDocumentEdit: Fetching document...');
        // Load document
        const docResponse = await DocumentApi.getDocument(id);
        if (!docResponse.ok) {
          throw new Error(`Failed to load document: ${docResponse.status}`);
        }
        const docData = await docResponse.json();
        
        if (abort) return;
        console.log('MobileDocumentEdit: Document loaded:', docData);
        setDoc(docData);

        console.log('MobileDocumentEdit: Fetching metadata...');
        // Load metadata
        const metaResponse = await DocumentMetadataApi.getDocumentMetadata(id);
        if (metaResponse.ok) {
          const metaData = await metaResponse.json();
          if (!abort) {
            console.log('MobileDocumentEdit: Metadata loaded:', metaData);
            setMetadata(sortMetadata(metaData || []));
          }
        }

        console.log('MobileDocumentEdit: Fetching associated courses...');
        // Load associated courses
        try {
          const userId = user.id || user.userId;
          const courses = await DocumentEndeavorApi.getExistingEndeavorsForDocument(id, userId);
          if (!abort) {
            console.log('MobileDocumentEdit: Associated courses loaded:', courses);
            setAssociatedCourses(courses || []);
          }
        } catch (error) {
          console.error('MobileDocumentEdit: Error loading associated courses:', error);
        }

        console.log('MobileDocumentEdit: Loading completed');
      } catch (error) {
        console.error('MobileDocumentEdit: Error loading document:', error);
        if (!abort) {
          setError(error.message || 'Failed to load document');
          setDoc(null);
          setMetadata([]);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    };

    loadDocument();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id, user?.userId]);

  const handleMetadataChange = (metadataId, newValue) => {
    setMetadataChanges(prev => ({
      ...prev,
      [metadataId]: newValue
    }));
    setHasChanges(true);
  };

  const getCurrentValue = (field) => {
    const metadataId = field.id || field.metadataId;
    if (metadataChanges.hasOwnProperty(metadataId)) {
      return metadataChanges[metadataId];
    }
    return field.value || field.metadataValue || '';
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError(null);

      // Save metadata changes
      const promises = Object.entries(metadataChanges).map(async ([metadataId, newValue]) => {
        try {
          await DocumentMetadataApi.updateDocumentMetadata(id, metadataId, { value: newValue });
        } catch (error) {
          console.error(`Failed to update metadata ${metadataId}:`, error);
          throw error;
        }
      });

      await Promise.all(promises);

      // Update local state
      setMetadata(prev => prev.map(field => {
        const metadataId = field.id || field.metadataId;
        if (metadataChanges.hasOwnProperty(metadataId)) {
          return {
            ...field,
            value: metadataChanges[metadataId],
            metadataValue: metadataChanges[metadataId]
          };
        }
        return field;
      }));

      setMetadataChanges({});
      setHasChanges(false);

      // Show success feedback
      alert('Document metadata updated successfully!');

    } catch (error) {
      console.error('Error saving document:', error);
      setError(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/m/document/${id}`);
      }
    } else {
      navigate(`/m/document/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="m-doc-edit-screen">
        <div className="m-doc-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Loading...</h1>
        </div>
        <div className="m-doc-edit-loading">
          <div className="m-loading-spinner"></div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-doc-edit-screen">
        <div className="m-doc-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="m-doc-edit-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="m-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="m-doc-edit-screen">
        <div className="m-doc-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Document Not Found</h1>
        </div>
        <div className="m-doc-edit-error">
          <p>The requested document could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="m-doc-edit-screen">
      <div className="m-doc-edit-header">
        <button className="m-back-btn" onClick={handleBackClick}>
          ← Back
        </button>
        <h1>Edit Document</h1>
        <div className="m-header-actions">
          <button className="m-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button 
            className={`m-save-btn ${hasChanges ? 'has-changes' : ''}`}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="m-doc-edit-content">
        <div className="m-doc-info">
          <h2>{doc.name || 'Untitled Document'}</h2>
          {associatedCourses.length > 0 && (
            <div className="m-courses-section">
              <h3>Associated Courses</h3>
              <div className="m-courses-list">
                {associatedCourses.map(course => (
                  <div key={course.id} className="m-course-tag">
                    {course.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="m-edit-fields">
          {metadata.length === 0 ? (
            <div className="m-no-metadata">
              <p>No metadata available for this document.</p>
            </div>
          ) : (
            metadata.map((field, index) => {
              const fieldName = field.metadata_definition?.name || field.metadataName || 'Unknown Field';
              const metadataId = field.id || field.metadataId;
              const currentValue = getCurrentValue(field);
              const isLongText = fieldName.toLowerCase().includes('text') || 
                               fieldName.toLowerCase().includes('summary') || 
                               fieldName.toLowerCase().includes('notes');
              
              return (
                <div key={metadataId || index} className="m-edit-field">
                  <label className="m-field-label">
                    {fieldName}
                  </label>
                  <div className="m-field-input">
                    {isLongText ? (
                      <textarea
                        value={currentValue}
                        onChange={(e) => handleMetadataChange(metadataId, e.target.value)}
                        placeholder={`Enter ${fieldName.toLowerCase()}...`}
                        className="m-textarea"
                        rows={currentValue ? Math.min(Math.max(currentValue.split('\n').length, 3), 10) : 3}
                      />
                    ) : (
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handleMetadataChange(metadataId, e.target.value)}
                        placeholder={`Enter ${fieldName.toLowerCase()}...`}
                        className="m-input"
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="m-unsaved-changes-bar">
          <span>You have unsaved changes</span>
          <div className="m-bar-actions">
            <button onClick={handleCancel} className="m-discard-btn">
              Discard
            </button>
            <button onClick={handleSave} className="m-save-bar-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
